"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { ArrowRightIcon } from "@heroicons/react/24/outline";
import hardnessData from "@/lib/data/waterHardnessCanada.json";
import Breadcrumbs from "@/components/shared/Breadcrumbs";
import DrinkingWaterQualityExplainer from "@/components/shared/DrinkingWaterQualityExplainer";
import WaterHardnessFAQ from "@/components/shared/WaterHardnessFAQ";
import { QuickAssessment } from "@/components/shared/QuickAssessment";
import { TrackedToolLink } from "@/components/shared/ContextualToolEntry";
import { getTrackedShopUrl } from "@/lib/external-links";
import { TrackingEvents } from "@/lib/analytics";
import { SHOP_COLLECTION_HANDLES, getShopCollectionPath } from "@/lib/shopify-catalog";
import {
  WATER_DOCTOR_CORE,
  WATER_DOCTOR_ORGANIZATION_ID,
} from "@/lib/schema/waterDoctorIdentity";
import {
  buildHelpMeChooseHref,
  buildWaterHardnessUrl,
  resolveHardnessDeepLink,
} from "./deep-links";
import { getHardnessLevel } from "./hardness-guidance";

const PROVINCES = hardnessData.map(({ province }) => province);

const HARDNESS_REFERENCE_ROWS = [
  {
    label: "Soft",
    gpg: "Less than 3.5 gpg",
    ppm: "Less than 60 ppm",
  },
  {
    label: "Moderate",
    gpg: "3.5 to under 7 gpg",
    ppm: "60 to under 120 ppm",
  },
  {
    label: "Hard",
    gpg: "7 to under 10.5 gpg",
    ppm: "120 to under 180 ppm",
  },
  {
    label: "Very Hard",
    gpg: "10.5 gpg or higher",
    ppm: "180 ppm or higher",
  },
];

const QUICK_GUIDANCE = [
  "Use postal code when you want the fastest lookup.",
  "Use province and city when you already know the municipality.",
  "Use assessment when fit, install, or mixed symptoms are still unclear.",
];

const HARDNESS_BADGE_CLASSES = {
  Soft: "border-blue-200 bg-blue-50 text-blue-700",
  Moderate: "border-amber-200 bg-amber-50 text-amber-700",
  Hard: "border-orange-200 bg-orange-50 text-orange-700",
  "Very Hard": "border-red-200 bg-red-50 text-red-700",
  Variable: "border-violet-200 bg-violet-50 text-violet-700",
  Unknown: "border-slate-200 bg-slate-50 text-slate-700",
};

const PRIMARY_CTA_CLASS =
  "inline-flex items-center justify-center gap-2 rounded-xl bg-sky-600 px-5 py-3 text-sm font-semibold text-white shadow-[0_16px_36px_rgba(2,132,199,0.22)] transition-colors duration-200 hover:bg-sky-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 touch-manipulation";
const SECONDARY_CTA_CLASS =
  "inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-900 shadow-sm transition-colors duration-200 hover:border-sky-200 hover:bg-sky-50 hover:text-sky-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 touch-manipulation";
const TEXT_CTA_CLASS =
  "inline-flex items-center gap-2 text-sm font-semibold text-sky-700 transition-colors duration-200 hover:text-sky-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 rounded-md touch-manipulation";

const ENTRY_SOFTENER_COLLECTION_URL = getTrackedShopUrl(
  getShopCollectionPath(SHOP_COLLECTION_HANDLES.waterSofteners),
  {
    utm_campaign: "water_hardness",
    utm_content: "tool_entry_shop",
  },
);

function InlineActionLink({
  href,
  label,
  className,
  dataUtmContent,
  tool,
  placement,
  intent,
  resultLevel,
  actionId,
}) {
  const sharedProps = {
    className,
    ...(dataUtmContent ? { "data-utm-content": dataUtmContent } : {}),
  };

  const content = (
    <>
      <span>{label}</span>
      <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
    </>
  );

  const handleClick = actionId && resultLevel ? () => {
    TrackingEvents.trackCustom("hardness_result_action", {
      data: {
        event_category: "product_discovery",
        hardness_level: resultLevel.toLowerCase().replace(/\s+/g, "_"),
        action: actionId,
        page_path: "/water-hardness",
      },
      platforms: { facebook: false, twitter: false },
      customMappings: {
        hardness_result_action: {
          google: "hardness_result_action",
          vercel: "hardness_result_action",
        },
      },
    });
  } : undefined;

  if (href.startsWith("http")) {
    return (
      <a href={href} onClick={handleClick} {...sharedProps}>
        {content}
      </a>
    );
  }

  if (tool) {
    return (
      <TrackedToolLink
        href={href}
        tool={tool}
        placement={placement}
        intent={intent}
        pagePath="/water-hardness"
        onClick={handleClick}
        {...sharedProps}
      >
        {content}
      </TrackedToolLink>
    );
  }

  return (
    <Link href={href} onClick={handleClick} {...sharedProps}>
      {content}
    </Link>
  );
}

function toTrackingTerm(value = "") {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-");
}

function formatPostalCode(input) {
  const cleaned = input.replace(/\s/g, "").toUpperCase();

  if (cleaned.length > 3) {
    return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)}`;
  }

  return cleaned;
}

function validatePostalCode(code) {
  const postalCodeRegex = /^[A-Za-z]\d[A-Za-z] \d[A-Za-z]\d$/;
  const fullFormat = code.length === 7;

  if (!fullFormat) {
    return "Please enter a complete postal code.";
  }

  if (!postalCodeRegex.test(code)) {
    return "Invalid postal code format. Use A1A 1A1.";
  }

  return "";
}

function getHardnessIcon(hardness) {
  const hardnessLevel = getHardnessLevel(hardness);

  if (hardnessLevel === "Soft") {
    return (
      <div className="flex h-full w-full items-center justify-center rounded-full bg-blue-100 text-blue-600">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="h-24 w-24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9.75 3.104v5.714a2.25 2.25 0 0 1-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 0 1 4.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0 1 12 15a9.065 9.065 0 0 1-6.23-.693L5 14.5m14.8.8 1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0 1 12 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5"
          />
        </svg>
      </div>
    );
  }

  if (hardnessLevel === "Moderate") {
    return (
      <div className="flex h-full w-full items-center justify-center rounded-full bg-yellow-100 text-yellow-600">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="h-24 w-24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9.75 3.104v5.714a2.25 2.25 0 0 1-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 0 1 4.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0 1 12 15a9.065 9.065 0 0 1-6.23-.693L5 14.5m14.8.8 1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0 1 12 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M20 14.5L17.5 12M20 14.5V18M20 14.5L22.5 12M4 14.5L6.5 12M4 14.5V18M4 14.5L1.5 12"
          />
        </svg>
      </div>
    );
  }

  if (hardnessLevel === "Hard") {
    return (
      <div className="flex h-full w-full items-center justify-center rounded-full bg-orange-100 text-orange-600">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="h-24 w-24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
          />
        </svg>
      </div>
    );
  }

  if (hardnessLevel === "Very Hard") {
    return (
      <div className="flex h-full w-full items-center justify-center rounded-full bg-red-100 text-red-600">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="h-24 w-24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"
          />
        </svg>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full items-center justify-center rounded-full bg-slate-100 text-slate-600">
      <span className="text-5xl font-semibold" aria-hidden="true">
        ?
      </span>
    </div>
  );
}

function getHardnessDescription(hardness) {
  const hardnessLevel = getHardnessLevel(hardness);

  if (hardnessLevel === "Soft") {
    return "Soft water contains low mineral content, typically less than 3.5 grains per gallon (gpg). It is gentle on pipes and appliances, though some homeowners notice a slippery feel when washing.";
  }

  if (hardnessLevel === "Moderate") {
    return "Moderate water hardness sits between 3.5 and 7 gpg. It can still create scale and soap-efficiency issues, but the right next step often depends on how strong the symptoms already feel in the home.";
  }

  if (hardnessLevel === "Hard") {
    return "Hard water usually contains enough calcium and magnesium to create scale, reduce soap performance, and put more stress on plumbing fixtures and appliances.";
  }

  if (hardnessLevel === "Very Hard") {
    return "Very hard water can create more scale and cleaning friction. The effect on a particular appliance depends on its use, temperature, and maintenance.";
  }

  if (hardnessLevel === "Variable") {
    return "The published range crosses hardness categories. It describes variation in the local reference, not a measured result for your property; confirm your own water before sizing equipment.";
  }

  return "This reference cannot be placed in a single hardness category. Confirm your property's water before choosing equipment.";
}

function createSearchResult(province, city, hardness) {
  return {
    province,
    city,
    hardness,
    hardnessLevel: getHardnessLevel(hardness),
  };
}

function findProvinceData(provinceName) {
  return hardnessData.find(({ province }) => province === provinceName);
}

function findProvinceMatch(provinceName) {
  const normalizedProvince = provinceName.toLowerCase();

  return hardnessData.find(
    ({ province }) =>
      province.toLowerCase() === normalizedProvince ||
      province.toLowerCase().includes(normalizedProvince) ||
      normalizedProvince.includes(province.toLowerCase()),
  );
}

function findCityMatch(provinceData, cityName) {
  const normalizedCity = cityName.toLowerCase();

  return provinceData?.cities.find(
    ({ name }) =>
      name.toLowerCase() === normalizedCity ||
      name.toLowerCase().includes(normalizedCity) ||
      normalizedCity.includes(name.toLowerCase()),
  );
}

function getResultContent(result) {
  const trackingTerm = toTrackingTerm(result.city);

  if (result.hardnessLevel === "Soft") {
    return {
      title: "Soft water benefits:",
      bullets: [
        "Less scale buildup in pipes and appliances",
        "Better soap lathering and cleaning efficiency",
        "Less spotting on dishes and fixtures",
        "Less hardness-related soap residue",
      ],
      recommendation:
        "Your water is already soft, so a water softener is usually not necessary. If you still have taste, odour, or contaminant concerns, shift the decision toward drinking-water filtration or expert diagnosis instead.",
      recommendationClasses: "border-green-100 bg-green-50 text-green-800",
      nextStepNote:
        "You likely do not need a whole-home softener. If the water still bothers you, the next step is usually drinking-water filtration or diagnosis.",
      items: [
        {
          icon: "diagnose",
          tone: "emerald",
          label: "Guided Recommendation",
          title: "Narrow down what to treat next",
          description:
            "Answer four quick questions about taste, odour, safety, and treatment scope without assuming hardness is the problem.",
          helperText:
            "Best when the water still bothers you but a softener is not the right starting point.",
          href: buildHelpMeChooseHref(result),
          cta: "Help Me Choose",
          tool: "help-me-choose",
          placement: "hardness-soft-result",
          intent: "soft-water-next-step",
          actionId: "help_me_choose",
        },
        {
          icon: "shop",
          tone: "slate",
          label: "Ready-to-Buy Path",
          title: "Shop RO and filtration systems",
          description:
            "Go straight to the store if you already know you want to improve drinking-water quality without solving a hardness problem.",
          helperText:
            "Use this when you are ready to compare models, pricing, and checkout.",
          href: getTrackedShopUrl(getShopCollectionPath(SHOP_COLLECTION_HANDLES.reverseOsmosisSystems), {
            utm_campaign: "water_hardness_result",
            utm_content: "soft_ro_shop",
            utm_term: trackingTerm,
          }),
          cta: "Shop RO Systems",
          dataUtmContent: "water_hardness_soft_ro_shop",
          actionId: "ro_shop",
        },
        {
          icon: "support",
          tone: "sky",
          label: "Advisory Path",
          title: "Talk to Water Doctor",
          description:
            "Use expert help when softness is fine but your water still has an unusual taste, smell, stain, or source-specific issue.",
          helperText:
            "Best for mixed symptoms that are not explained by hardness alone.",
          href: "/contact",
          cta: "Talk to an Expert",
          actionId: "expert_contact",
        },
      ],
      footnote:
        "Hardness is only one part of water quality. Taste, odour, staining, or source-water concerns may call for a different treatment.",
    };
  }

  if (result.hardnessLevel === "Moderate") {
    return {
      title: "Moderately hard water effects:",
      bullets: [
        "May cause some scale buildup over time",
        "Can reduce appliance efficiency slightly",
        "Provides beneficial minerals while still creating some whole-home friction",
      ],
      recommendation:
        "A water softener may still be worthwhile if you already notice scale, soap inefficiency, or appliance wear, but moderate water often needs a sizing conversation before a direct purchase.",
      recommendationClasses: "border-yellow-100 bg-yellow-50 text-yellow-800",
      nextStepNote:
        "Moderate hardness can still justify treatment, but the best option depends on how strong the symptoms already feel in your home.",
      items: [
        {
          icon: "assessment",
          tone: "sky",
          label: "Guided Service Path",
          title: "Book a free assessment",
          description:
            "Best when you want help deciding whether moderate hardness justifies a softener or whether another treatment path makes more sense.",
          helperText: "Primary CTA when the right solution is still uncertain.",
          href: "#result-assessment",
          cta: "Request Free Assessment",
          actionId: "assessment",
        },
        {
          icon: "diagnose",
          tone: "emerald",
          label: "Comparison Path",
          title: "See the water softener guide",
          description:
            "Review hard-water symptoms, product types, and service expectations before moving further into shopping.",
          helperText:
            "Useful when you want more context before looking at products.",
          href: "/services/water-softener#compare",
          cta: "Open Softener Guide",
          actionId: "softener_guide",
        },
        {
          icon: "shop",
          tone: "slate",
          label: "Ready-to-Buy Path",
          title: "Shop water softeners",
          description:
            "Go straight to the collection if you already know moderate hardness is causing enough friction to warrant a system.",
          helperText:
            "Best when you are ready to compare systems and pricing now.",
          href: getTrackedShopUrl(getShopCollectionPath(SHOP_COLLECTION_HANDLES.waterSofteners), {
            utm_campaign: "water_hardness_result",
            utm_content: "moderate_softener_shop",
            utm_term: trackingTerm,
          }),
          cta: "Shop Water Softeners",
          actionId: "softener_shop",
          dataUtmContent: "water_hardness_moderate_softener_shop",
        },
      ],
      footnote:
        "If the symptoms are mild, compare the guide first. If scale and appliance wear are already clear, move on to system sizing.",
    };
  }

  if (result.hardnessLevel === "Unknown" || result.hardnessLevel === "Variable") {
    const variable = result.hardnessLevel === "Variable";
    return {
      title: variable ? "This location spans more than one hardness band:" : "This result needs manual review:",
      bullets: [
        variable ? "The published range cannot represent one property's measured hardness" : "The location data matched, but the published hardness value is not a simple numeric band",
        "The safest next step is diagnosis before you shop",
        "Confirm the source, property conditions, and symptoms before selecting treatment",
      ],
      recommendation:
        variable ? "Your local reference crosses treatment bands. Test at your property or request an assessment before choosing a system." : "Use assessment or expert support first when the published number is unclear.",
      recommendationClasses: "border-slate-200 bg-slate-50 text-slate-700",
      nextStepNote:
        "This result needs a closer look before you choose a product or treatment path.",
      items: [
        {
          icon: "assessment",
          tone: "sky",
          label: "Guided Service Path",
          title: "Book a free assessment",
          description:
            "Best when you want Water Doctor to interpret the local source, your plumbing symptoms, and the right treatment category.",
          helperText:
            "Use this when a published municipal number is not enough to shop confidently.",
          href: "#result-assessment",
          cta: "Request Free Assessment",
          actionId: "assessment",
        },
        {
          icon: "support",
          tone: "emerald",
          label: "Advisory Path",
          title: "Talk to Water Doctor",
          description:
            "Use expert support when you need help separating hardness from taste, odour, well-water, or plumbing-specific issues.",
          helperText:
            "Good fit when the problem is real but the local data does not cleanly answer it.",
          href: "/contact",
          cta: "Talk to an Expert",
          actionId: "expert_contact",
        },
      ],
      footnote:
        "Bring the published source information and the symptoms you notice at home to an assessment before choosing equipment.",
    };
  }

  return {
    title: `${result.hardnessLevel === "Very Hard" ? "Very hard" : "Hard"} water can cause:`,
    bullets: [
      "Scale buildup in pipes and appliances",
      "Reduced efficiency of water heaters",
      "Spots on dishes and fixtures",
      "Difficulty lathering soap and detergents",
      "More soap residue after washing",
    ],
    recommendation:
      "A water softener is often the clearest category to evaluate because hard-water minerals can create scale, spotting, and cleaning friction. Final sizing still depends on household demand and installation conditions.",
    recommendationClasses: "border-blue-100 bg-blue-50 text-blue-800",
    nextStepNote:
      "This level usually means a softener is worth serious consideration, especially if you already notice scale or cleaning friction.",
    items: [
      {
        icon: "assessment",
        tone: "sky",
        label: "Guided Service Path",
        title: "Book a free assessment",
        description:
          "Best for sizing, installation planning, and confirming the right system for your plumbing setup and household demand.",
        helperText:
          "Primary CTA when you want Water Doctor to help you choose and plan the install.",
        href: "#result-assessment",
        cta: "Request Free Assessment",
        actionId: "assessment",
      },
      {
        icon: "diagnose",
        tone: "emerald",
        label: "Comparison Path",
        title: "See the water softener guide",
        description:
          "Review hard-water symptoms, system types, and treatment tradeoffs before deciding which kind of softener to buy.",
        helperText:
          "Useful when you want more context before entering the store.",
        href: "/services/water-softener#compare",
        cta: "Open Softener Guide",
        actionId: "softener_guide",
      },
      {
        icon: "shop",
        tone: "slate",
        label: "Ready-to-Buy Path",
        title: "Shop water softeners",
        description:
          "Go directly to Shopify if you already know a softener is the right category and want to compare systems, prices, and variants.",
        helperText: "Best when you are ready to compare models and pricing.",
        href: getTrackedShopUrl(getShopCollectionPath(SHOP_COLLECTION_HANDLES.waterSofteners), {
          utm_campaign: "water_hardness_result",
          utm_content: "hard_softener_shop",
          utm_term: trackingTerm,
        }),
        cta: "Shop Water Softeners",
        actionId: "softener_shop",
        dataUtmContent: "water_hardness_hard_softener_shop",
      },
    ],
    footnote:
      "Book an assessment for sizing and installation help, or compare softeners directly when you already know the right treatment category.",
  };
}

export default function WaterHardnessPage() {
  const [selectedProvince, setSelectedProvince] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [searchResult, setSearchResult] = useState(null);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [postalCodeError, setPostalCodeError] = useState("");
  const postalInputRef = useRef(null);
  const lastTrackedResultRef = useRef(null);

  const availableCities = selectedProvince
    ? (findProvinceData(selectedProvince)?.cities.map(({ name }) => name) ?? [])
    : [];

  const resultContent = searchResult ? getResultContent(searchResult) : null;
  const waterHardnessExplanation = searchResult
    ? getHardnessDescription(searchResult.hardness)
    : "";
  const actionButtons = resultContent?.items.slice(0, 2) ?? [];
  const textActions = resultContent?.items.slice(2) ?? [];

  useEffect(() => {
    if (!searchPerformed || !searchResult) {
      lastTrackedResultRef.current = null;
      return;
    }

    const resultKey = `${searchResult.province}:${searchResult.city}:${searchResult.hardness}`;
    if (lastTrackedResultRef.current === resultKey) return;
    lastTrackedResultRef.current = resultKey;

    TrackingEvents.trackCustom("hardness_result_view", {
      data: {
        event_category: "product_discovery",
        hardness_level: searchResult.hardnessLevel.toLowerCase().replace(/\s+/g, "_"),
        page_path: "/water-hardness",
      },
      platforms: { facebook: false, twitter: false },
      customMappings: {
        hardness_result_view: {
          google: "hardness_result_view",
          vercel: "hardness_result_view",
        },
      },
    });
  }, [searchPerformed, searchResult]);

  useEffect(() => {
    let focusFrame;

    function applyLocationFromUrl() {
      const deepLink = resolveHardnessDeepLink(
        window.location.search,
        hardnessData,
      );
      const result = deepLink.result
        ? createSearchResult(
            deepLink.result.province,
            deepLink.result.city,
            deepLink.result.hardness,
          )
        : null;

      setSelectedProvince(deepLink.province);
      setSelectedCity(deepLink.city);
      setSearchResult(result);
      setSearchPerformed(deepLink.shouldAutoRun);
      setError("");

      if (deepLink.shouldFocusPostal) {
        focusFrame = window.requestAnimationFrame(() => {
          postalInputRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          postalInputRef.current?.focus({ preventScroll: true });
        });
      }
    }

    applyLocationFromUrl();
    window.addEventListener("popstate", applyLocationFromUrl);

    return () => {
      window.removeEventListener("popstate", applyLocationFromUrl);
      if (focusFrame) window.cancelAnimationFrame(focusFrame);
    };
  }, []);

  function replaceLocationUrl(province, city = "") {
    const href = buildWaterHardnessUrl(window.location.href, {
      province,
      city,
    });

    window.history.replaceState(window.history.state, "", href);
  }

  function clearResultForLocationChange(province = "") {
    setSearchResult(null);
    setSearchPerformed(false);
    setError("");
    replaceLocationUrl(province);
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Canadian Water Hardness Checker",
    applicationCategory: "UtilitiesApplication",
    operatingSystem: "Web Browser",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "CAD",
    },
    provider: {
      "@id": WATER_DOCTOR_ORGANIZATION_ID,
      "@type": "Organization",
      name: WATER_DOCTOR_CORE.name,
      url: WATER_DOCTOR_CORE.url,
      logo: WATER_DOCTOR_CORE.logo,
      contactPoint: {
        "@type": "ContactPoint",
        telephone: WATER_DOCTOR_CORE.telephone,
        contactType: "customer service",
        areaServed: "CA",
      },
    },
    featureList: [
      "Search by postal code",
      "Search by province and city",
      "Water hardness classification",
      "Treatment recommendations",
      "Canadian water quality data",
    ],
  };

  function handlePostalCodeChange(event) {
    const formatted = formatPostalCode(event.target.value);
    setPostalCode(formatted);
    setError("");

    if (formatted.length === 7) {
      setPostalCodeError(validatePostalCode(formatted));
      return;
    }

    setPostalCodeError("");
  }

  function handleProvinceAndCitySearch(event) {
    event.preventDefault();
    setSearchPerformed(true);
    setError("");
    setSearchResult(null);

    if (!selectedProvince || !selectedCity) {
      setError("Please select both province and city.");
      return;
    }

    const provinceData = findProvinceData(selectedProvince);
    const cityData = provinceData?.cities.find(
      ({ name }) => name === selectedCity,
    );

    if (!cityData) {
      setError("No water hardness data was found for the selected city.");
      return;
    }

    const result = createSearchResult(
      selectedProvince,
      selectedCity,
      cityData.hardness,
    );

    setSearchResult(result);
    replaceLocationUrl(result.province, result.city);
  }

  async function handlePostalCodeSearch(event) {
    event.preventDefault();
    setSearchPerformed(true);
    setError("");
    setSearchResult(null);

    const validationError = validatePostalCode(postalCode);
    if (validationError) {
      setError(validationError);
      setPostalCodeError(validationError);
      return;
    }

    setIsLoading(true);

    try {
      const formattedPostalCode = postalCode.replace(/\s/g, "");
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(formattedPostalCode)}.json?country=ca&types=postcode&access_token=pk.eyJ1IjoidGVkY2FvY29tbyIsImEiOiJjbTgxeW0wOGYwbWdsMmlwenVpY2x0d244In0.0EI4_0iJRISomZ78wOOS5Q`,
      );

      if (!response.ok) {
        throw new Error(`Mapbox request failed with ${response.status}`);
      }

      const data = await response.json();

      if (!data.features || data.features.length === 0) {
        setError("Could not find a location for this postal code.");
        return;
      }

      const feature = data.features[0];
      const context = feature.context || [];
      let province = "";
      let city = "";

      for (const item of context) {
        if (item.id.startsWith("region")) {
          province = item.text;
        } else if (item.id.startsWith("place")) {
          city = item.text;
        }
      }

      if (!province || !city) {
        setError(
          "Could not determine the city and province for this postal code.",
        );
        return;
      }

      const provinceData = findProvinceMatch(province);
      if (!provinceData) {
        setError(`No water hardness data is available for ${province}.`);
        return;
      }

      const cityData = findCityMatch(provinceData, city);
      if (!cityData) {
        setError(
          `No water hardness data is available for ${city}, ${province}.`,
        );
        return;
      }

      setSelectedProvince(provinceData.province);
      setSelectedCity(cityData.name);
      const result = createSearchResult(
        provinceData.province,
        cityData.name,
        cityData.hardness,
      );
      setSearchResult(result);
      replaceLocationUrl(result.province, result.city);
    } catch (searchError) {
      console.error("Error fetching location data:", searchError);
      setError("An error occurred while searching. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }

  const breadcrumbItems = [
    { name: "Home", url: "https://waterdoctor.ca" },
    {
      name: "Water Hardness Checker",
      url: "https://waterdoctor.ca/water-hardness",
    },
  ];

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Breadcrumbs items={breadcrumbItems} />

      <main id="main-content" className="bg-white">
        <section className="border-b border-sky-100 bg-[linear-gradient(180deg,#f7fcff_0%,#ffffff_100%)]">
          <div className="mx-auto max-w-7xl px-4 py-7 sm:px-6 sm:py-10 lg:px-8">
            <p className="text-[0.8125rem] font-semibold uppercase tracking-[0.1em] text-water-teal">Free diagnostic tool</p>
            <div className="mt-3 grid gap-5 lg:grid-cols-[minmax(0,0.85fr)_minmax(20rem,0.55fr)] lg:items-end">
              <div>
                <h1 className="max-w-3xl text-balance text-3xl font-semibold tracking-[-0.035em] text-water-ink sm:text-5xl">Check your local water hardness</h1>
                <p className="mt-3 max-w-2xl text-base leading-7 text-water-muted sm:text-lg">Search by city or Canadian postal code, then see what the local reference value can mean at home. A postal search finds a city-level estimate; it does not test your tap water.</p>
              </div>
              <ul className="flex flex-wrap gap-x-5 gap-y-1 border-t border-sky-100 pt-3 text-sm leading-6 text-water-muted lg:grid lg:border-l-2 lg:border-t-0 lg:pl-5 lg:pt-0">
                <li>No email required</li>
                <li>Consultation appears only after a result</li>
              </ul>
            </div>
          </div>
        </section>

        <section
          id="hardness-search"
          className="scroll-mt-32 mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-12 lg:px-8 lg:py-14"
        >
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[0.92fr_1.08fr] lg:gap-10">
            <div className="order-2 rounded-[2rem] border border-sky-100 bg-[linear-gradient(180deg,rgba(248,252,255,0.98)_0%,rgba(255,255,255,1)_100%)] p-6 shadow-[0_24px_64px_rgba(15,23,42,0.08)] sm:p-8 lg:order-1">
              <h2 className="text-balance text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
                Check Your Water Hardness and What To Do Next
              </h2>
              <p className="mt-4 text-base leading-7 text-gray-600">
                Search once, understand your hardness level, and decide whether
                the right next step is a water softener, a drinking-water
                solution, or a guided assessment.
              </p>

              <div className="mt-6 rounded-[1.5rem] border border-white/80 bg-white/90 p-5 shadow-sm">
                <p className="text-sm font-semibold uppercase tracking-[0.16em] text-sky-700">
                  Quick Guidance
                </p>
                <ul className="mt-3 space-y-2 text-sm leading-6 text-slate-600">
                  {QUICK_GUIDANCE.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <span className="mt-2 h-2 w-2 rounded-full bg-sky-500" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-8">
                <div className="flex items-center justify-between gap-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Understanding Water Hardness
                  </h3>
                  <span className="rounded-full border border-sky-100 bg-sky-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-sky-700">
                    Diagnosis Guide
                  </span>
                </div>

                <div className="mt-4 overflow-x-auto rounded-[1.5rem] border border-slate-200 bg-white shadow-sm">
                  <table className="min-w-full overflow-hidden">
                    <thead>
                      <tr className="bg-slate-50/80">
                        <th className="border-b px-4 py-3 text-left text-sm font-semibold text-gray-900">
                          Classification
                        </th>
                        <th className="border-b px-4 py-3 text-left text-sm font-semibold text-gray-900">
                          Grains Per Gallon (gpg)
                        </th>
                        <th className="border-b px-4 py-3 text-left text-sm font-semibold text-gray-900">
                          Parts Per Million (ppm)
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {HARDNESS_REFERENCE_ROWS.map((row, index) => (
                        <tr key={row.label}>
                          <td
                            className={`px-4 py-3 text-sm text-gray-900 ${
                              index < HARDNESS_REFERENCE_ROWS.length - 1
                                ? "border-b"
                                : ""
                            }`}
                          >
                            {row.label}
                          </td>
                          <td
                            className={`px-4 py-3 text-sm text-gray-900 ${
                              index < HARDNESS_REFERENCE_ROWS.length - 1
                                ? "border-b"
                                : ""
                            }`}
                          >
                            {row.gpg}
                          </td>
                          <td
                            className={`px-4 py-3 text-sm text-gray-900 ${
                              index < HARDNESS_REFERENCE_ROWS.length - 1
                                ? "border-b"
                                : ""
                            }`}
                          >
                            {row.ppm}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <p className="mt-6 text-base leading-7 text-gray-600">
                  Hard water can create limescale in pipes, reduce water-heater
                  efficiency, leave spots on dishes, and make showers, laundry,
                  and cleaning feel harder than they should.
                </p>
              </div>

            </div>

            <div className="order-1 lg:order-2">
              <div className="rounded-[2rem] border border-slate-200 bg-white/95 p-6 shadow-[0_24px_64px_rgba(15,23,42,0.08)] backdrop-blur sm:p-8">
                <div className="mb-4">
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-[0.18em] text-sky-600">
                      Tool Input
                    </p>
                    <h2 className="mt-2 text-2xl font-bold tracking-tight text-gray-900">
                      Search Your Location
                    </h2>
                  </div>
                </div>

                <p className="max-w-2xl text-sm leading-6 text-slate-600">Choose a municipality, or use a Canadian postal code to find the matching city-level reference. Both options use the same hardness dataset.</p>

                <div className="mt-5 space-y-8">
                  <form
                    onSubmit={handleProvinceAndCitySearch}
                    className="rounded-[1.5rem] border border-slate-200 bg-slate-50/50 p-5"
                  >
                    <div className="mb-4 flex items-start justify-between gap-3">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">
                          Option 1: Search by Province and City
                        </h3>
                        <p className="mt-1 text-sm leading-6 text-slate-600">
                          Best when you already know the municipality you want
                          to check.
                        </p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label
                          htmlFor="province"
                          className="mb-1 block text-sm font-medium text-gray-700"
                        >
                          Province
                        </label>
                        <select
                          id="province"
                          name="province"
                          autoComplete="address-level1"
                          value={selectedProvince}
                          onChange={(event) => {
                            const province = event.target.value;

                            setSelectedProvince(province);
                            setSelectedCity("");
                            clearResultForLocationChange(province);
                          }}
                          className="min-h-[52px] w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-slate-900 shadow-sm transition-colors duration-200 focus:border-sky-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2"
                        >
                          <option value="">Select Province</option>
                          {PROVINCES.map((province) => (
                            <option key={province} value={province}>
                              {province}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label
                          htmlFor="city"
                          className="mb-1 block text-sm font-medium text-gray-700"
                        >
                          City
                        </label>
                        <select
                          id="city"
                          name="city"
                          autoComplete="address-level2"
                          value={selectedCity}
                          onChange={(event) => {
                            setSelectedCity(event.target.value);
                            clearResultForLocationChange(selectedProvince);
                          }}
                          className="min-h-[52px] w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-slate-900 shadow-sm transition-colors duration-200 focus:border-sky-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-100"
                          disabled={!selectedProvince}
                        >
                          <option value="">Select City</option>
                          {availableCities.map((city) => (
                            <option key={city} value={city}>
                              {city}
                            </option>
                          ))}
                        </select>
                      </div>

                      <button
                        type="submit"
                        className="inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-sky-600 px-4 py-3 text-sm font-semibold text-white shadow-[0_16px_36px_rgba(2,132,199,0.22)] transition-colors duration-200 hover:bg-sky-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300 touch-manipulation"
                        disabled={!selectedProvince || !selectedCity}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="h-5 w-5"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
                          />
                        </svg>
                        Search by Province and City
                      </button>
                    </div>
                  </form>

                  <div className="relative py-2">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300" />
                    </div>
                    <div className="relative flex justify-center">
                      <span className="bg-white px-3 text-sm font-medium text-gray-500">
                        OR
                      </span>
                    </div>
                  </div>

                  <form
                    onSubmit={handlePostalCodeSearch}
                    className="rounded-[1.5rem] border border-slate-200 bg-slate-50/50 p-5"
                    aria-busy={isLoading}
                  >
                    <div className="mb-4 flex items-start justify-between gap-3">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">
                          Option 2: Search by Postal Code
                        </h3>
                        <p className="mt-1 text-sm leading-6 text-slate-600">
                          Fastest option when you want the quickest lookup and
                          next-step routing.
                        </p>
                      </div>
                      <span className="rounded-full border border-sky-100 bg-sky-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-sky-700">
                        Recommended
                      </span>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label
                          htmlFor="postal"
                          className="mb-1 block text-sm font-medium text-gray-700"
                        >
                          Postal Code
                        </label>
                        <div className="relative">
                          <input
                            ref={postalInputRef}
                            type="text"
                            id="postal"
                            name="postalCode"
                            autoComplete="postal-code"
                            autoCapitalize="characters"
                            spellCheck={false}
                            inputMode="text"
                            value={postalCode}
                            onChange={handlePostalCodeChange}
                            placeholder="A1A 1A1…"
                            aria-invalid={postalCodeError ? "true" : "false"}
                            aria-describedby={
                              postalCodeError
                                ? "postal-help postal-error"
                                : "postal-help"
                            }
                            className={`min-h-[52px] w-full rounded-xl border px-4 py-3 pr-10 text-slate-900 shadow-sm transition-colors duration-200 focus:border-sky-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 ${
                              postalCodeError
                                ? "border-red-300"
                                : "border-gray-300"
                            }`}
                            maxLength={7}
                          />
                          {postalCode.length === 7 && !postalCodeError ? (
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3 text-green-500">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="h-5 w-5"
                                aria-hidden="true"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                                />
                              </svg>
                            </div>
                          ) : null}
                        </div>
                        <p
                          id="postal-help"
                          className="mt-1 text-sm text-gray-500"
                        >
                          Format: A1A 1A1
                        </p>
                        {postalCodeError ? (
                          <p
                            id="postal-error"
                            className="mt-1 text-sm text-red-600"
                            aria-live="polite"
                          >
                            {postalCodeError}
                          </p>
                        ) : null}
                      </div>

                      <button
                        type="submit"
                        className="inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-sky-600 px-4 py-3 text-sm font-semibold text-white shadow-[0_16px_36px_rgba(2,132,199,0.22)] transition-colors duration-200 hover:bg-sky-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300 touch-manipulation"
                        disabled={
                          !postalCode || Boolean(postalCodeError) || isLoading
                        }
                      >
                        {isLoading ? (
                          <>
                            <svg
                              className="h-5 w-5 animate-spin"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              aria-hidden="true"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              />
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4Zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647Z"
                              />
                            </svg>
                            Searching…
                          </>
                        ) : (
                          <>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                              className="h-5 w-5"
                              aria-hidden="true"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
                              />
                            </svg>
                            Search by Postal Code
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>

                {error ? (
                  <div
                    className="mt-6 rounded-xl border border-red-200 bg-red-50 p-4"
                    aria-live="polite"
                  >
                    <p className="flex items-center text-sm text-red-700">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="mr-2 h-5 w-5"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
                        />
                      </svg>
                      {error}
                    </p>
                  </div>
                ) : null}

                <div className="mt-8 border-t border-slate-200 pt-6">
                  <p className="text-sm leading-6 text-slate-600">
                    Already know your path? Skip the lookup and go straight into
                    assessment, shopping, or the softener guide.
                  </p>
                  <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                    <InlineActionLink
                      href="/forms/home-assessment"
                      label="Request Free Assessment"
                      className={PRIMARY_CTA_CLASS}
                    />
                    <InlineActionLink
                      href={ENTRY_SOFTENER_COLLECTION_URL}
                      label="Shop Water Softeners"
                      className={SECONDARY_CTA_CLASS}
                      dataUtmContent="water_hardness_tool_entry_shop"
                    />
                    <InlineActionLink
                      href="/services/water-softener"
                      label="See Softener Guide"
                      className={TEXT_CTA_CLASS}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {searchPerformed && resultContent && searchResult ? (
            <div
              id="hardness-results"
              className="mt-8 scroll-mt-28 rounded-[1.9rem] border border-slate-200 bg-[linear-gradient(180deg,rgba(248,250,252,0.98)_0%,rgba(255,255,255,1)_100%)] p-6 shadow-sm sm:p-8"
              aria-live="polite"
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold uppercase tracking-[0.16em] text-sky-700">
                    Your Result
                  </p>
                  <h3 className="mt-2 text-balance text-2xl font-bold text-slate-950 sm:text-3xl">
                    What this means for your next step
                  </h3>
                </div>
                <span
                  className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] ${HARDNESS_BADGE_CLASSES[searchResult.hardnessLevel] || HARDNESS_BADGE_CLASSES.Unknown}`}
                >
                  {searchResult.hardnessLevel}
                </span>
              </div>

              <div className="mt-6 grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
                <div className="rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex items-center gap-5">
                    <div className="relative h-20 w-20 flex-shrink-0">
                      {getHardnessIcon(searchResult.hardness)}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold uppercase tracking-[0.16em] text-sky-700">
                        {searchResult.city}, {searchResult.province}
                      </p>
                      <p className="mt-2 text-base font-medium text-slate-950">
                        Water Hardness: {searchResult.hardness}
                      </p>
                    </div>
                  </div>

                  <p className="mt-4 text-sm leading-6 text-slate-600">
                    {waterHardnessExplanation}
                  </p>
                  <p className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-6 text-slate-600">
                    This is a city-level reference, not an in-home test. The dataset does not include a source citation or observation date for every city. Supply zones, seasons, and private wells can differ. It does not identify chlorine, metals, microorganisms, or plumbing conditions. Check your current utility report or test a property sample before sizing a system.
                  </p>
                  <p className="mt-3 text-sm leading-6 text-slate-700">
                    {resultContent.nextStepNote}
                  </p>

                  <details className="mt-5 rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                    <summary className="cursor-pointer text-sm font-semibold text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 rounded-md">
                      {resultContent.title}
                    </summary>
                    <ul className="mt-3 list-disc space-y-1 pl-5 text-sm leading-6 text-slate-600">
                      {resultContent.bullets.map((bullet) => (
                        <li key={bullet}>{bullet}</li>
                      ))}
                    </ul>
                  </details>
                </div>

                <div className="rounded-[1.5rem] border border-sky-100 bg-[linear-gradient(180deg,rgba(240,249,255,0.82)_0%,rgba(255,255,255,0.98)_100%)] p-5 shadow-sm">
                  <p className="text-sm font-semibold uppercase tracking-[0.16em] text-sky-700">
                    Recommended Next Step
                  </p>
                  <div
                    className={`mt-4 rounded-2xl border p-4 text-sm leading-6 ${resultContent.recommendationClasses}`}
                  >
                    <p className="font-semibold">{resultContent.recommendation}</p>
                  </div>

                  <div className="mt-5 grid gap-3 sm:grid-cols-2">
                    {actionButtons.map((item, index) => (
                      <InlineActionLink
                        key={`${item.cta}-${item.href}`}
                        href={item.href}
                        label={item.cta}
                        className={
                          index === 0 ? PRIMARY_CTA_CLASS : SECONDARY_CTA_CLASS
                        }
                        dataUtmContent={item.dataUtmContent}
                        tool={item.tool}
                        placement={item.placement}
                        intent={item.intent}
                        resultLevel={searchResult.hardnessLevel}
                        actionId={item.actionId}
                      />
                    ))}
                  </div>

                  {textActions.length > 0 ? (
                    <div className="mt-4 flex flex-wrap gap-4">
                      {textActions.map((item) => (
                        <InlineActionLink
                          key={`${item.cta}-${item.href}`}
                          href={item.href}
                          label={item.cta}
                          className={TEXT_CTA_CLASS}
                          dataUtmContent={item.dataUtmContent}
                          tool={item.tool}
                          placement={item.placement}
                          intent={item.intent}
                          resultLevel={searchResult.hardnessLevel}
                          actionId={item.actionId}
                        />
                      ))}
                    </div>
                  ) : null}

                  <div id="result-assessment" className="mt-7 scroll-mt-28 border-t border-water-border pt-7">
                    <p className="text-[0.8125rem] font-semibold uppercase tracking-[0.08em] text-water-blue">Need help interpreting this result?</p>
                    <h4 className="mt-2 text-xl font-semibold text-water-ink">Ask for a product and installation recommendation</h4>
                    <p className="mt-2 text-sm leading-6 text-water-muted">Send the basics here. Your city and hardness result stay attached to the request.</p>
                    <div className="mt-5">
                      <QuickAssessment
                        mode="residential"
                        service="water-hardness"
                        pagePath="/water-hardness"
                        placement="result"
                        city={`${searchResult.city}, ${searchResult.province}`}
                        contentId={`${searchResult.hardnessLevel}:${searchResult.hardness}`}
                        ctaVariant="hardness-result"
                        diagnostic={{
                          tool: "water-hardness",
                          province: searchResult.province,
                          city: searchResult.city,
                          hardness: searchResult.hardness,
                          hardnessLevel: searchResult.hardnessLevel,
                          resultId: `${toTrackingTerm(searchResult.city)}-${toTrackingTerm(searchResult.hardnessLevel)}`,
                          version: "2026-08",
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </section>

        <section className="border-t border-slate-100 bg-white py-12 sm:py-16">
          <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <details className="group rounded-[1.75rem] border border-slate-200 bg-slate-50/75 p-6 shadow-sm">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2">
                <div>
                  <p className="text-sm font-semibold uppercase tracking-[0.18em] text-sky-700">
                    Deeper Reading
                  </p>
                  <h2 className="mt-2 text-xl font-bold tracking-tight text-slate-950 sm:text-2xl">
                    Explore drinking-water treatment beyond hardness.
                  </h2>
                  <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600">
                    Continue to the drinking-water guide for help comparing
                    chlorine taste and odour, lead context, municipal reports,
                    activated carbon, and reverse osmosis.
                  </p>
                </div>
                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-700 transition-transform duration-200 group-open:rotate-45">
                  +
                </span>
              </summary>
              <div className="mt-6 border-t border-slate-200 pt-6">
                <DrinkingWaterQualityExplainer className="border-t-0 py-0 sm:py-0" />
              </div>
            </details>
          </div>
        </section>
        <WaterHardnessFAQ />
      </main>
    </>
  );
}
